import React from 'react';
import { calculateHitRate } from '../utils/bettingUtils';
import { calculatePlayerPerformanceScore } from '../utils/playerUtils';

const PlayerStats = ({
  players,
  playerName,
  selectedStat,
  line,
  activeFilters,
  selectedGameSpan,
  setSelectedGameSpan,
  getFilteredGames,
  gamesDisplayed = 64  // Default to 64 games if not specified
}) => {
  if (!players || !playerName) {
    return null;
  }

  // Get player games using the same filtering logic as StatChart
  const playerGames = getFilteredGames(players, playerName, activeFilters)
    .sort((a, b) => {
      // Sort by year descending first
      const yearDiff = parseInt(b.year) - parseInt(a.year);
      if (yearDiff !== 0) return yearDiff;
      
      // Then by round number descending
      const roundA = parseInt(a.round.replace(/\D/g, '')) || 0;
      const roundB = parseInt(b.round.replace(/\D/g, '')) || 0;
      return roundB - roundA;
    });
  
  if (playerGames.length === 0) {
    return (
      <div className="w-full text-center text-gray-500 dark:text-gray-400 py-2">
        No game data available
      </div>
    );
  }
  
  // Limit to games that would be displayed in the chart
  const displayedGames = playerGames.slice(0, gamesDisplayed);
  
  // Define spans of games to analyze, using the filtered games
  const spans = [
    { label: 'L3', games: playerGames.slice(0, 3), span: 3 },
    { label: 'L5', games: playerGames.slice(0, 5), span: 5 },
    { label: 'L10', games: playerGames.slice(0, 10), span: 10 },
    { label: 'All', games: displayedGames, span: null }
  ];
  
  // Update hit rates calculation with filtered games directly
  const calculateHitRates = (games) => {
    if (!games || games.length === 0) {
      return { percentage: 0, record: '0/0' };
    }
    
    const hits = games.filter(game => {
      let value;
      if (selectedStat === 'performance_points') {
        value = calculatePlayerPerformanceScore(game);
      } else {
        value = parseFloat(game[selectedStat]) || 0;
      }
      return value > line;
    }).length;
    
    const total = games.length;
    return {
      percentage: total > 0 ? (hits / total) * 100 : 0,
      record: `${hits}/${total}`
    };
  };
  
  return (
    <div className="overflow-hidden">
      <div className="flex flex-nowrap gap-1 min-w-0">
        {spans.map((span, index) => {
          if (span.games.length === 0) return null;
          
          // Calculate hit rate for this span using the filtered games
          const hitRateResult = calculateHitRates(span.games);
          const impliedProbability = hitRateResult.percentage / 100;
          const impliedOdds = impliedProbability > 0 ? (1 / impliedProbability).toFixed(2) : 'N/A';
          
          const isSelected = selectedGameSpan === span.span;
          
          return (
            <div 
              key={index} 
              onClick={() => setSelectedGameSpan(isSelected ? null : span.span)}
              className={`
                flex-1 bg-white dark:bg-gray-800 rounded-lg border transition-colors cursor-pointer
                ${isSelected ? 'ring-2 ring-blue-500' : 'border-gray-200 dark:border-gray-700 hover:border-blue-300 dark:hover:border-blue-600'}
              `}
            >
              <div className="flex items-center justify-between px-1.5 sm:px-3 py-1.5 sm:py-2">
                <div className="flex items-center gap-1 sm:gap-2">
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">{span.label}:</span>
                  <span className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-400">
                    {hitRateResult.record} ({(hitRateResult.percentage).toFixed(0)}%)
                  </span>
                  <span className="hidden sm:inline text-xs sm:text-sm font-medium text-gray-900 dark:text-gray-100">
                    ${impliedOdds}
                  </span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PlayerStats; 